import { Avatar, Stack, Typography, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { GenericDialog } from "src/components/generic-dialog";
import { SaveAndCancelButton } from "src/components/save-and-cancel-button";
import { useDropzone } from "src/hooks";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useProfileSettingsPageGeneralSectionT } from "src/translation-hooks/profile-settings-page-general-section";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { profileSettingsActions } from "../../profile-settings-store";

const mapStateToProps = (state: RootState) => ({
  isProfilePictureDialogOpen: state.pageReducer.profileReducer.profileSettingsReducer.isProfilePictureDialogOpen,
  contactPersonDetails: state.generalReducer.applicationReducer.contactPersonDetails
});

const ownActions = {
  setIsProfilePictureDialogOpen: profileSettingsActions.setIsProfilePictureDialogOpen,
  postContactPersonPictureRequest: profileSettingsActions.postContactPersonPictureRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProfilePictureUploadDialogBase = (props: Props) => {
  const { postContactPersonPictureRequest, setIsProfilePictureDialogOpen, contactPersonDetails } = props;

  const theme = useTheme();

  const [droppedFile, setDroppedFile] = useState<File>();
  const [fileError, setFileError] = useState<string>();

  const validationErrorsT = useValidationMessageTranslationsT();
  const genericTranslationsT = useGenericTranslationsT();
  const profileSettingsPageGeneralSectionT = useProfileSettingsPageGeneralSectionT();

  const handleDrop = useCallback((accepted: File[]) => {
    setDroppedFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: 20971520,
    multiple: false,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/svg+xml": [],
      "image/heic": []
    },
    onDropAccepted: () => {
      setFileError("");
    },
    onDropRejected: file => {
      switch (file[0]?.errors[0].code) {
        case "file-invalid-type":
          setFileError(validationErrorsT.fileTypeNotSupported);
          break;
        case "file-too-large":
          setFileError(validationErrorsT.fileTooLarge);
          break;
        default:
          setFileError(file[0]?.errors[0]?.code);
      }
    },
    onDrop: handleDrop
  });

  const handleSave = useCallback(() => {
    if (!droppedFile || contactPersonDetails.loading !== "has-loaded") return;

    postContactPersonPictureRequest(contactPersonDetails.data.contactPersonId, droppedFile);
  }, [droppedFile, contactPersonDetails]);

  const handleClose = useCallback(() => {
    setIsProfilePictureDialogOpen(false);
  }, []);

  return (
    <GenericDialog headerTitle={profileSettingsPageGeneralSectionT.editProfilePicture} onClose={handleClose} renderDialogActions={<SaveAndCancelButton onSave={handleSave} onCancel={handleClose} />}>
      <Stack gap={2} alignItems="center">
        {droppedFile && <Avatar src={droppedFile && URL.createObjectURL(droppedFile)} sx={{ height: 140, width: 140 }} />}
        {/* File dropzone */}
        <Stack
          gap={1}
          width="100%"
          justifyContent="center"
          alignItems="center"
          paddingY={4}
          border={`${isDragActive ? "2px" : "1px"} dashed black`}
          sx={{ cursor: "pointer", backgroundColor: isDragActive ? theme.palette.grey["300"] : theme.palette.grey["100"] }}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Typography variant="body1">{genericTranslationsT.uploadFileHint(".jpeg, .jpg, .png")}</Typography>
          <Typography variant="caption">{genericTranslationsT.uploadFileRule("20 MB")}</Typography>
          {droppedFile && (
            <Typography variant="body1" fontWeight="500">
              {genericTranslationsT.selectedFile(droppedFile.name)}
            </Typography>
          )}
          {fileError && <Typography color="error">{fileError}</Typography>}
        </Stack>
      </Stack>
    </GenericDialog>
  );
};

export const ProfilePictureUploadDialog = connect(mapStateToProps, ownActions)(ProfilePictureUploadDialogBase);
